/* IBM Confidential‌ - OCO Source Materials‌ - (C) COPYRIGHT IBM CORP. 2020-2024 - The source code for this program is not published or otherwise‌ divested of its trade secrets, irrespective of what has been‌ deposited with the U.S. Copyright Office. */

import React, { Component } from "react";
import UserAvatar from "@carbon/icons-react/lib/user--avatar/20";
import Home from "@carbon/icons-react/lib/home/20";
import ReportData from "@carbon/icons-react/lib/report--data/20";
import RequestQuote24 from "@carbon/icons-react/lib/request-quote/20"
import Tools from "@carbon/icons-react/lib/tools/20";
import Workspace from "@carbon/icons-react/lib/workspace/20";
import DocumentTask from "@carbon/icons-react/lib/document--tasks/20";
import Document from "@carbon/icons-react/lib/document/20";
import Portfolio from "@carbon/icons-react/lib/portfolio/20";
import Box from "@carbon/icons-react/lib/box/20";
import InventoryManagement from "@carbon/icons-react/lib/inventory-management/20";
import Earth from "@carbon/icons-react/lib/earth/20";
import Apps from "@carbon/icons-react/lib/apps/16";
import Network from "@carbon/icons-react/lib/network--2/20";
import Switcher from "@carbon/icons-react/lib/switcher/20";
import Grid from "@carbon/icons-react/lib/grid/20";
import { ReactComponent as MPRToggle } from '../icons/3D-MPR-Toggle.svg';
import { ReactComponent as StackedScrolling1 } from '../icons/stacked-scrolling--1.svg';
import { HierarchyListItemHandler } from "../handlers/HierarchyListItemHandler";
import { NavRoutingHandler } from "../handlers/NavRoutingHandler";
import { ActiveMenuItemHandler } from "../handlers/ActiveMenuItemHandler";
import { ProjectContextService } from "../services/ProjectContextService";
import { UnsavedDataService } from "../services/UnsavedDataService";
import { AppMsg, NavConfig } from "../utils";

import {
  HeaderContainer,
  Header,
  SideNav
} from "carbon-addons-iot-react";
import ProjectContainer from "../project/ProjectContainer.js";
import UserProfile from "../user/UserProfile.js";
import { about } from "../about/About.js";
import { help } from "../help/Help.js";
import { MultiSessionLogoutModal, handleLogout } from "../logout/MultiSessionLogout.js";
import "./NavMain.scss";
import SessionExpiredModal from "../session/SessionExpiredModal";

const COMPANY_LEVEL_PROJECT_ID = 1;
const SUB_CAT_NOT_NECESSARY = -1;
const MENU_ITEM_NAME_NOT_NECESSARY = null;
const NOT_FROM_BROWSER_ACTION = false;
const SUPPRESS_ICON = true;
const DO_NOT_SUPPRESS_ICON = false;

const DEFAULT_TITLE_PREFIX = "IBM";
const DEFAULT_TITLE_APP = "TRIRIGA";
const DEFAULT_TITLE = DEFAULT_TITLE_PREFIX + " " + DEFAULT_TITLE_APP;

const SUITE_CATALOG_MENU_ITEM_ID = '-1002';

const SMALL_BREAKPOINT_WIDTH = 864;

class NavMain extends Component {
  constructor(props) {
    super(props);
    this.state = {
      menuItems: [],
    };
    this.hamburgerEventAdded = false;
    this.tririgaContextUrl = this._getContextUrl();
    this.sideNavRefDiv = React.createRef();
    this.projectContainerDivRef = React.createRef();
    this.projectContainer = React.createRef();
    this.switchProject = this.switchProject.bind(this);
    this.doLogout = this.doLogout.bind(this);
    this.sitemapMenuItemButtonClick = this.sitemapMenuItemButtonClick.bind(this);
    this.handleNavFrameSourceForSubPage = this.handleNavFrameSourceForSubPage.bind(this);
    this.handleValidMenuItem = this.handleValidMenuItem.bind(this);
    this.hierarchyListItemHandler = new HierarchyListItemHandler();
    this.unsavedDataService = new UnsavedDataService(this.hierarchyListItemHandler);
    this.projectContextService = new ProjectContextService(this, this.projectContainer);
    this.projectSwitcherPortals = [];
    this.projectSideNav = null;
    this.activeMenuItemHandler = new ActiveMenuItemHandler(this.hierarchyListItemHandler);
    this.navRoutingHandler = new NavRoutingHandler(this);
    this.contextProps = NavConfig.getNavConfigData();
    this.catalogMenu = null;
    this.hamburgerButton = null;
    this.sideNavElement = [];
    this.dir = "ltr";
  }

  componentDidMount() {

    var contextProps = this.contextProps;
    
    //Check for no company level access force log out.
    if (contextProps.isForceLogOutOnNoCompanyLevelAccess) {
      this._validateCompanyLevelAccess();
      return;
    }


      //fetching NavItems. Other fetches can potentially be called simultaneously here.
      Promise.all([
        fetch("../../api/v1/navCollection/currentUserDefaultMenu")
      ])
        .then(([resp1]) => {
          return Promise.all([resp1.json()]);
        })
        .then(([data]) => {
          let navItems = data.navItems;
          this._handleSideNavLinks(navItems, DO_NOT_SUPPRESS_ICON);

          //Site map items
          this.hierarchyListItemHandler.handleFromMain(navItems);

          //Suite Catalog menu
          this.catalogMenu = this._getCatalogMenu();

          this.setState({
            menuItems: navItems
          });

          //Handle project context properties
          this.projectContextService.handleProjectContextProperties(
            contextProps.projectId,
            contextProps.projectName,
            contextProps.priorProjectId,
            contextProps.priorProjectName
          );

          //Handle project menu, and handle any routing on callback.
          this.projectContextService.handleShowProjectMenu(
            contextProps.projectId,
            () => {
              this._handleNavigateRoute();
            }
          );

          //Hamburger button
          this.hamburgerButton = window.document.querySelector(".bx--header__menu-toggle");

          //side nav handle style handle
          this.sideNavElement = document.getElementsByClassName(
            "bx--side-nav__navigation"
          )[0];

          //Setting aria-hidden to false to make the sidenav items accessible to assistive technologies
          //Fixes D92952 - Change in state is not informed to screen reader users when clicked on 
          //Reports option in navigation links .
          this.sideNavElement.setAttribute('aria-hidden', "false");

          //Adding title attribute to sideNav elements with No child elements
          let sideNavLabels = document.getElementsByClassName(
            "bx--side-nav__link"
          );
          for (let i = 0; i < sideNavLabels.length; i++) {
            sideNavLabels[i].setAttribute("title", sideNavLabels[i].innerText);
          }

          //User profile menu
          let userProfileMenu = document.getElementsByClassName(
            "bx--header__submenu"
          );
          userProfileMenu[0].setAttribute(
            "role",
            "menu"
          );

          //Setting title for User-Profile.
          let userProfileBtn = document.getElementsByClassName(
            "bx--header__menu-title"
          );
          userProfileBtn[0].setAttribute(
            "title",
            AppMsg.getMessage(AppMsg.USER_USER)
          );

          //Setting title for Help and About
          let headerActionItems = document.getElementsByClassName(
            "bx--header-action-btn"
          );
          for (let i = 0; i < headerActionItems.length; i++) {
            if (
              headerActionItems[i].getAttribute("aria-label") ===
              AppMsg.getMessage(AppMsg.APP_ABOUT)
            ) {
              headerActionItems[i].setAttribute(
                "title",
                AppMsg.getMessage(AppMsg.APP_ABOUT)
              );
            }

            if (
              headerActionItems[i].getAttribute("aria-label") ===
              AppMsg.getMessage(AppMsg.APP_HELP)
            ) {
              headerActionItems[i].setAttribute(
                "title",
                AppMsg.getMessage(AppMsg.APP_HELP)
              );
            }
          }

          //Detect browser back, forward, and refresh.
          window.addEventListener(
            "popstate",
            this._handleNavigateRouteOnBrowserAction.bind(this)
          );

          this._handleRTL();
        });


    //Handle project switcher menu
    this.projectContextService.handleProjectSwitcherMenu();
  }

  componentDidUpdate() {
    //componentDidUpdate will run with every render, this check is to prevent adding event listener more than once 
    if(!this.hamburgerEventAdded) {
      this.hamburgerEventAdded = true;
      //initial rendering will set expanded to false for hamburger button
      if (this.hamburgerButton == null) {     
        this.hamburgerButton = window.document.querySelector(".bx--header__menu-toggle");
      }

      this.hamburgerButton.setAttribute('aria-expanded', "false");

      //adding event listener to toggle EXPANDED state of hamburgen button
      this.hamburgerButton.addEventListener('click', () => {
        const isExpanded = this.hamburgerButton.getAttribute('aria-expanded');
        if(isExpanded === "true") {
          this.hamburgerButton.setAttribute("aria-expanded", "false");
          this._enableHeaderTabFocus();
         } else {
          this.hamburgerButton.setAttribute("aria-expanded", "true");
          this.sideNavElement.style.display = 'flex'; //Ensure display as it may have been turned off by another action.
          this._disableHeaderTabFocus();
        }
      });

      this._initHeaderTabFocusListeners();

      window.onresize = () => {
        if (this.sideNavElement && this.sideNavElement.style && window.top.innerWidth > SMALL_BREAKPOINT_WIDTH) { 
          this.sideNavElement.style.display = 'flex';
        }
      }
    }

    this._handleDuplicateElementIds();
  }

  _initHeaderTabFocusListeners() {
    this.hamburgerButton.onfocus = () => {
      const isExpanded = this.hamburgerButton.getAttribute('aria-expanded');
      if(isExpanded === "true") {
        this._disableHeaderTabFocus();
      } else {
        this._enableHeaderTabFocus();
      }
    };

    let headerBar = document.querySelector(".bx--header__global");
    if (headerBar !== null) {
      headerBar.onclick = () => {
        this._enableHeaderTabFocus();
      }
    }
  }

  _enableHeaderTabFocus() {
    document.querySelectorAll(".bx--header__name").forEach(item => {
      item.tabIndex = "0";
    });

    document.querySelectorAll(".bx--header-action-btn.bx--header__action").forEach(item => {
      item.tabIndex = "0";
    });

    document.querySelectorAll(".bx--header__menu-title").forEach(item => {
      item.tabIndex = "0";
    });
  }

  _disableHeaderTabFocus() {
    document.querySelectorAll(".bx--header__name").forEach(item => {
      item.tabIndex = "-1";
    });

    document.querySelectorAll(".bx--header-action-btn.bx--header__action").forEach(item => {
      item.tabIndex = "-1";
    });

    document.querySelectorAll(".bx--header__menu-title").forEach(item => {
      item.tabIndex = "-1";
    });
  }

  _handleDuplicateElementIds() {
    let idCounter = 0;
    document.querySelectorAll("#icon").forEach(element => {
      if (idCounter > 0) {
        element.id = element.id + "_" + idCounter;
      }
      idCounter++;
    });

    idCounter = 0;
    document.querySelectorAll("#_Transparent_Rectangle_").forEach(element => {
      if (idCounter > 0) {
        element.id = element.id + "_" + idCounter;
      }
      idCounter++;
    });
  }

  _handleRTL() {
    if (NavConfig.getNavConfigData().isRTL) {
      this.dir = "rtl";
      document.getElementsByTagName('html')[0].setAttribute("dir", "rtl");
    }
  }
  componentWillUnmount() {
    //Clear any listeners
    window.removeEventListener('popstate', this._handleNavigateRouteOnBrowserAction.bind(this));
  }

  switchProjectAndSelectProjectMenuItem(projectId, menuItemName, isFromBrowserAction) {
    this.switchProject(SUB_CAT_NOT_NECESSARY, projectId, menuItemName, isFromBrowserAction);
  }

  //Switch to different project
  switchProject(subCatId, projectId, menuItemName, isFromBrowserAction, navHandleCallback) {
    this.projectContextService.switchProject(subCatId, projectId, menuItemName, isFromBrowserAction, navHandleCallback);
  }

  _getMenuItems() {
    if (this.state.menuItems.length < 1) {
      return [];
    }
    let menuItems = this.state.menuItems;
    return menuItems;
  }

  _handleSideNavLinks(menuItems, isSuppressIcon) {
    for (let i = 0; i < menuItems.length; i++) {
      if (menuItems[i].name === "sysSuiteCatalog") {
        //ignore suite catalog.
        menuItems[i].isEnabled = false;
      }
      if (menuItems[i].metaData) {
        let id = menuItems[i].id;
        let name = menuItems[i].name;
        if (menuItems[i].childContent) {
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          menuItems[i].linkContent = <a 
            
            //Set the link href to support right click browser open in new window/tab.
            href={
              this.contextProps.isAccessibilityEnabled
                ? undefined
                : this.tririgaContextUrl + menuItems[i].metaData.url
            }
           
            title={menuItems[i].content} id={menuItems[i].id}
            tabIndex="0" aria-label={menuItems[i].content}
            onKeyUp={(event) => {
                this._navButtonClickFromEnterKey(event, id);
            }}
            onKeyDown={(event) => {
              if (event.keyCode === 13) {
                //Prevent default so expand/collapse does not happen on enter click.
                event.preventDefault();
              } 
            }}
            onClick={(event) => {
              this.navButtonClickFromCompanyContext(id, NOT_FROM_BROWSER_ACTION, event);
              window.document.querySelector("#triNavigation").focus();
              //Stop propagation so expand/collapse does not happen.
              event.stopPropagation();
            }}
            onBlur={(event) => {
              if (event.keyCode === 13) { //Only needed on enter.
                window.document.querySelector("#triNavigation").focus();
              }
              //Stop propagation so expand/collapse does not happen.
              event.stopPropagation();
            }}
            className="sideNavMenuItemWithChildren">
            <div className="sideNavMenuItemWithChildrenLabel">
              {menuItems[i].content}
            </div>
          </a>
        } else {
          menuItems[i].metaData.href=this.tririgaContextUrl+menuItems[i].metaData.url
          menuItems[i].metaData.onBlur = (event) => {
            //Stop propagation so expand/collapse does not happen when focus is lost.
            event.stopPropagation();
          };
          menuItems[i].metaData.onClick = (event) => { 
            this.navButtonClickFromCompanyContext(id, NOT_FROM_BROWSER_ACTION, event);
            window.document.querySelector("#triNavigation").focus(); 
          };
        }   
        
        //Getting icons for NavItems
        if (isSuppressIcon) {
          menuItems[i].icon = '';
        } else {
          let iconPath = menuItems[i].icon;
          if (iconPath === "") {
            menuItems[i].icon = this._getIcon(name);
          }
          else {
            //Path of icon as per Tririga
            let path = this._getContextUrl() + '/getCompanyFile.jsp?fileLoc=' + iconPath;
            let variable = () => {
              return (<img className='icon-color' src={`${path}`} width="16px" innerheight="16px" alt="User Profile"/>)
            }
            menuItems[i].icon = variable;
          }
        }
      }

      //Iterate through children
      if (menuItems[i].childContent) {
        this._handleSideNavLinks(menuItems[i].childContent, SUPPRESS_ICON)
      }
    }
  }

  _ensureCompanyLevel(navHandleCallback) {
    if (this.projectContextService.isCurrentProjectAtProjectLevelContext()) {
      //Always force clicks from side nav to company level. 
      //Company Level has project id of 1.
      this.switchProject(SUB_CAT_NOT_NECESSARY,
        COMPANY_LEVEL_PROJECT_ID,
        MENU_ITEM_NAME_NOT_NECESSARY,
        NOT_FROM_BROWSER_ACTION,
        navHandleCallback);

        //Reset active project sub menu ids.
        this.projectContextService.resetActiveProjectSubMenuIds();
    } else {
      if (navHandleCallback) {
        navHandleCallback();
      }
    }
  }

  //Handle enter key click
  _navButtonClickFromEnterKey(event, menuId) {
    //keyCode 13 is the enter key
    if (event.keyCode === 13) {
      this.navButtonClickFromCompanyContext(menuId, NOT_FROM_BROWSER_ACTION, event);
      window.document.querySelector("#triNavigation").focus();
    }
  }

  //Clicks from company level sitemap
  sitemapMenuItemButtonClick(menuId, event) {
    this.navButtonClickFromCompanyContext(menuId, NOT_FROM_BROWSER_ACTION, event);
  }

  navButtonClickFromCompanyContext(menuId, isFromBrowserAction, event) {
    //Handle href reset if necessary.
    this._handleMenuItemHrefReset(event);

    //First check if navigation is canceled for unsaved data.
    if (this.unsavedDataService.isDataSaveNeeded(this.handleValidMenuItem(menuId, this.hierarchyListItemHandler))) {
      return;
    }

    //Handle active menu item style
    this.activeMenuItemHandler.handle(menuId);

    //Ensure company level on any side nav click
    this._ensureCompanyLevel(() => {
      this._validateCompanyLevelAccess();
      this.navButtonClick(menuId, this.hierarchyListItemHandler, isFromBrowserAction);

      //Hide side-nav if less than or equal to small break point width.
      if (this.sideNavElement && this.sideNavElement.style && window.top.innerWidth <= SMALL_BREAKPOINT_WIDTH) { 
        this.sideNavElement.style.display = 'none';
        if(this.hamburgerButton.getAttribute('aria-expanded') === "true") {
          this.hamburgerButton.click();
        }
      }
    });
  }

  //Nav button click from main side nav
  navButtonClick(menuId, hierarchyListItemHandler, isFromBrowserAction, projectId) {

    //Retrieve valid menu item.
    let menuItem = this.handleValidMenuItem(menuId, hierarchyListItemHandler);
    if (menuItem.portalName != null && !projectId) {
      this.projectContextService.handleProjectRendering(menuItem.portalName);
    }
    this.projectContainer.current.navButtonClick(menuId, hierarchyListItemHandler, isFromBrowserAction);
  }

  _handleMenuItemHrefReset(event) {
    if (event) {
      //Reset current nav target href to assist with browser right open new window / new tab.
      //This ensures normal menu clicks do not trigger nav calls twice due to item having both onclick code and href url.
      window.currentNavTarget = event.currentTarget;
      event.currentTarget.href="javascript:resetNavUrl('"+event.currentTarget.href+"')";
    }
  }

  //Currently used by User Profile to set source and sub page bookmarks.
  handleNavFrameSourceForSubPage(menuItemUrl, menuItemName, isSubPageBookmark, isProjectContext, projectId, isFromBrowserAction) {
    //If we are not in project context handle active menu item and frame source ensuring company level.
    if ((isSubPageBookmark && !isProjectContext) || (!isSubPageBookmark && !this.projectContainer.current.isCurrentProjectAtProjectLevelContext())) {
      //Ensure company level
      this._ensureCompanyLevel(() => {
        //Handle active menu item, hide project header and handle frame source.
        this._validateCompanyLevelAccess();
        this.navRoutingHandler.handleActiveMenuItem(menuItemName);
        let currentSelectedMenuItemId = this.hierarchyListItemHandler.getListItemIdByName(menuItemName);
        this.projectContainer.current.handleNavFrameSourceForSubPage(menuItemUrl, menuItemName, isSubPageBookmark, isFromBrowserAction, currentSelectedMenuItemId);
      });
      return;
    } else if (isSubPageBookmark && projectId) {
      //If project id is passed in, switch to that project now.
      this.projectContextService.switchProjectForSubPageBookmark(SUB_CAT_NOT_NECESSARY, projectId, menuItemName);
    }
    this.projectContainer.current.handleNavFrameSourceForSubPage(menuItemUrl, menuItemName, isSubPageBookmark, isFromBrowserAction);
  }

  //Function for getting default icons for Navigation Items.
  _getIcon(linkContent) {
    let selectedIcon;
    switch (linkContent) {
      case '': selectedIcon = Home;
        break;
      case 'Builder+-+My+Reports': selectedIcon = ReportData;
        break;
      case 'Landing+Page+-+Requests': selectedIcon = RequestQuote24;
        break;
      case 'Landing+Page+-+Projects': selectedIcon = StackedScrolling1;
        break;
      case 'Landing+Page+-+Tasks': selectedIcon = DocumentTask;
        break;
      case 'Landing+Page+-+Space': selectedIcon = Workspace;
        break;
      case 'Landing+Page+-+Maintenance': selectedIcon = Tools;
        break;
      case 'Landing+Page+-+Sustainability': selectedIcon = Earth;
        break;
      case 'Landing+Page+-+Inventory': selectedIcon = MPRToggle;
        break;
      case 'Landing+Page+-+Contracts': selectedIcon = Document;
        break;
      case 'Landing+Page+-+Procurement': selectedIcon = InventoryManagement;
        break;
      case 'Landing+Page+-+Portfolio': selectedIcon = Portfolio;
        break;
      case 'Landing+Page+-+Tools': selectedIcon = Box;
        break;
      case 'sysSiteMap': selectedIcon = Network;
        break;
      default: selectedIcon = Apps;
    }
    return selectedIcon;
  }

  _getContextUrl() {
    return NavConfig.CONTEXT_URL;
  }

  _getHeaderActionItems() {
    let actionItemsArray = [];

    //If SHOW_ABOUT_LINK property in TRIRIGAWEB.properties is TRUE
    if (NavConfig.getNavConfigData().isShowAboutLink) {
      actionItemsArray.push(about(this.tririgaContextUrl));
    }

    //If SHOW_HELP_AND_SUPPORT_LINKS property in TRIRIGAWEB.properties is TRUE
    if (NavConfig.getNavConfigData().isShowHelpAndSupportLinks) {
      actionItemsArray.push(help(this.tririgaContextUrl));
    }

    actionItemsArray.push(
      {
        label: AppMsg.getMessage(AppMsg.USER_USER),
        btnContent:<UserAvatar fill="white" description="Icon" className="about-icon"/>,
        childContent: [
          {
            content: (
              <UserProfile 
                {...this.state}
                tririgaContextUrl={this.tririgaContextUrl} 
                handleNavFrameSourceForSubPage={this.handleNavFrameSourceForSubPage} 
                closeUserActionsMenu={this.closeUserActionsMenu}
                unsavedDataService={this.unsavedDataService}
                hierarchyListItemHandler={this.hierarchyListItemHandler}
                username={this.contextProps.userProfile.userAccount}
                dir={this.dir}              />
            ),
            metaData: {
              element: 'div'
            }
          },
          {
            metaData: {
              title: AppMsg.getMessage(AppMsg.APP_LOGOUT),
              target: '_blank',
              rel: 'noopener noreferrer',
              element: 'a',
              style: { textIndent: '16px' },
              onClick: () => { this._logOutClicked() },
              onKeyDown: (event) => { this._logOutFromEnterKey(event) }
            },
            content: AppMsg.getMessage(AppMsg.APP_LOGOUT),
          }

        ]
      }
    );

    //Add catalog menu if company level access is allowed.
   if (this.catalogMenu != null && this.contextProps.isCompanyLevelAccessAllowed) {
      actionItemsArray.push(this.catalogMenu);
    }

    return actionItemsArray;

  }

  _getCatalogMenu() {
    //If catalog menu list item does not exist, return null.
    if (this.hierarchyListItemHandler.getListItemById(SUITE_CATALOG_MENU_ITEM_ID) == null) {
      return null;
    }
    let catalogMenu = {
      label: AppMsg.getMessage(AppMsg.CATALOG_MENU),
      btnContent: <Switcher fill='white' className="about-icon"/>,
    }
    catalogMenu.childContent = [
      {
        content: <div style={{display:'flex', margin:'10px'}}><Grid/>{AppMsg.getMessage(AppMsg.SUITE_CATALOG)}</div>,
        metaData: {
          title: AppMsg.getMessage(AppMsg.CATALOG_MENU),
          target: '_blank',
          rel: 'noopener noreferrer',
          element: 'a',
          style: { textIndent: '16px' },
          onClick: () => {this._handleSuiteCatalogMenuItemClick();},
          onKeyDown: () => {this._handleSuiteCatalogMenuItemClick();}
        }
      }
    ]
    return catalogMenu;
  }

  _handleSuiteCatalogMenuItemClick() {
    //TODO: For suport to display in project context.
    // if (this.projectContextService.isCurrentProjectAtProjectLevelContext()) {
    //   this.projectContextService.selectProjectMenuItem(SUITE_CATALOG_MENU_ITEM_ID, NOT_FROM_BROWSER_ACTION);
    // } else {
    //   this.navButtonClickFromCompanyContext(SUITE_CATALOG_MENU_ITEM_ID, NOT_FROM_BROWSER_ACTION);
    // }
    this.navButtonClickFromCompanyContext(SUITE_CATALOG_MENU_ITEM_ID, NOT_FROM_BROWSER_ACTION);
    window.document.querySelector("#triNavigation").focus();
  }

  _validateCompanyLevelAccess() {
    if (!this.contextProps.isCompanyLevelAccessAllowed) {
      alert(AppMsg.getMessage(AppMsg.APP_NO_COMPANY));
      this.doLogout();
    }
  }

  closeUserActionsMenu() {
    document.getElementsByClassName('bx--header__menu-item')[0].setAttribute('aria-expanded', 'false');
  }

  _logOutClicked() {
    //First close user actions.
    this.closeUserActionsMenu();
    handleLogout();
  }

  doLogout() {
    fetch('../../ui/navContext/logOut')
      .then((resp) => resp.json())
      .then(data => {
        if (data.logoutURL) {
          window.location.href = data.logoutURL.startsWith("/") ? `${window.location.origin}` + data.logoutURL: data.logoutURL;
        }
      });
  }

  //Handle enter key click
  _logOutFromEnterKey(event) {
    //keyCode 13 is the enter key
    if (event.keyCode === 13) {
      this._logOutClicked();
    }
  }

  handleValidMenuItem(menuId, hierarchyListItemHandler) {
    return hierarchyListItemHandler.handleValidMenuItem(menuId);
  }

  _handleNavigateRouteOnBrowserAction() {
    this._handleNavigateRoute(true);
  }

  _handleNavigateRoute(isFromBrowserAction) {
    //Route handling
    this.navRoutingHandler = new NavRoutingHandler(this, isFromBrowserAction);
    this.navRoutingHandler.handle();
  }

  render() {
    return (

      <div className="app">
        {//Don't render until menu items exist.
          this.state.menuItems.length === 0 ?
            <div className="bx--header" style={{ height: '5px' }} /> :
            <HeaderContainer
              render={({ isSideNavExpanded, onClickSideNavExpand }) => (
                <>
                 <MultiSessionLogoutModal
                    doLogout={this.doLogout}
                  />
                  <SessionExpiredModal
                    doLogout={this.doLogout}
                    handleMultiSessionLogout={handleLogout}
                    username={this.contextProps.userProfile.userAccount} //User account used for user name login credentials.
                    loginObj={this.contextProps.loginObj}
                    languageId={this.contextProps.userLanguageId}
                  />
                  <Header
                    actionItems={this._getHeaderActionItems()}
                    prefix={this.contextProps.headerTitle === DEFAULT_TITLE ? DEFAULT_TITLE_PREFIX: ""}
                    appName={this.contextProps.headerTitle === DEFAULT_TITLE ? DEFAULT_TITLE_APP: this.contextProps.headerTitle}
                    isSideNavExpanded={isSideNavExpanded}
                    onClickSideNavExpand={onClickSideNavExpand}
                  />

                  <div ref={this.sideNavRefDiv} style={{ height: '100%', paddingTop: NavConfig.getNavConfigData().isCompanyLevelAccessAllowed ? '48px':'' }}>
                    {NavConfig.getNavConfigData().isCompanyLevelAccessAllowed ?


                      <SideNav
                        links={this._getMenuItems()}

                        isSideNavExpanded={isSideNavExpanded}
                        onClickSideNavExpand={onClickSideNavExpand}

                      />

                      : null}
                    <div className= {`iot--main-content ${NavConfig.getNavConfigData().isCompanyLevelAccessAllowed ? '':'no-company-level-access'}`}>
                      <div ref={this.projectContainerDivRef} className="projectContainerDiv" id="main-content">
                        <ProjectContainer ref={this.projectContainer}
                          {...this.state}
                          switchProject={this.switchProject}
                          projectSideNav={this.projectSideNav}
                          handleValidMenuItem={this.handleValidMenuItem}
                          tririgaContextUrl={this.tririgaContextUrl}
                          sitemapMenuItemButtonClick={this.sitemapMenuItemButtonClick}
                          navRoutingHandler={this.navRoutingHandler}
                          unsavedDataService={this.unsavedDataService}
                          hierarchyListItemHandler={this.hierarchyListItemHandler}
                        />
                      </div>
                    </div>
                  </div>

                </>
              )}
            />
        }
      </div>
    );
  }
}

export default NavMain;
